import type { OutboundTemplate, TemplateStatus } from '../api/templates.js';
import type { ProviderTemplate } from '../api/providerTemplates.js';

/**
 * Detección de deriva entre nuestras plantillas y las del proveedor.
 *
 * El sync original (#161) sólo preguntaba si el par (templateName, language) ya
 * existía: si existía, mostraba "✓ Ya existe" y no comparaba nada más. Dos cosas
 * pasaban sin que nadie las viera:
 *
 *  - **Recategorización.** Meta reclasifica plantillas por su cuenta, típicamente
 *    de UTILITY a MARKETING, y eso cambia el precio por mensaje. Nosotros
 *    seguíamos mostrando UTILITY y la diferencia aparecía en la factura.
 *  - **Rechazo sobreviniente.** Meta puede rechazar o pausar una plantilla ya
 *    aprobada. Seguíamos creyéndola válida y una campaña completa fallaba
 *    mensaje por mensaje.
 *
 * Esto compara y clasifica; no decide. La decisión de aceptar el cambio es del
 * operador, igual que en el legacy.
 */

/** Estados del proveedor que conocemos, mapeados a los nuestros. */
const STATUS_CONOCIDOS: Record<string, TemplateStatus> = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
  // No tenemos 'paused' en el modelo local; se refleja como rechazado porque el
  // efecto es el mismo: no se puede enviar.
  PAUSED: 'rejected',
  DISABLED: 'rejected',
};

/** Estados en los que la plantilla NO sirve para enviar. */
const NO_ENVIABLES = new Set<TemplateStatus>(['rejected']);

export interface CampoCambiado {
  campo: 'category' | 'status';
  local: string;
  remoto: string;
}

export interface TemplateCambiado {
  local: OutboundTemplate;
  remote: ProviderTemplate;
  cambios: CampoCambiado[];
  /** La categoría cambió: cambia el precio por mensaje. */
  afectaCosto: boolean;
  /** Pasó a un estado desde el que no se puede enviar. */
  rompeEnvio: boolean;
  /** El proveedor devolvió un estado que no conocemos. */
  desconocido: boolean;
}

export interface TemplateNuevo {
  remote: ProviderTemplate;
}

export interface DiffResult {
  nuevos: TemplateNuevo[];
  cambiados: TemplateCambiado[];
  sinCambios: OutboundTemplate[];
  /** Locales que ya no existen en el proveedor. */
  huerfanos: OutboundTemplate[];
}

export function diffProviderTemplates(
  locales: ReadonlyArray<OutboundTemplate>,
  remotos: ReadonlyArray<ProviderTemplate>,
  connectorId: string,
): DiffResult {
  // Sólo comparamos dentro del connector filtrado: el mismo templateName puede
  // existir en dos WABA distintos y no son la misma plantilla.
  const propios = locales.filter((t) => t.connectorId === connectorId);
  const porClave = new Map(propios.map((t) => [clave(t.templateName, t.language), t]));

  const nuevos: TemplateNuevo[] = [];
  const cambiados: TemplateCambiado[] = [];
  const sinCambios: OutboundTemplate[] = [];
  const vistos = new Set<string>();

  for (const r of remotos) {
    const k = clave(r.name, r.language);
    vistos.add(k);
    const l = porClave.get(k);
    if (!l) {
      nuevos.push({ remote: r });
      continue;
    }

    const cambios: CampoCambiado[] = [];

    const catRemota = (r.category ?? '').toUpperCase();
    if (catRemota && catRemota !== l.category) {
      cambios.push({ campo: 'category', local: l.category, remoto: catRemota });
    }

    const statusRemotoRaw = (r.status ?? '').toUpperCase();
    const statusMapeado = STATUS_CONOCIDOS[statusRemotoRaw];
    // Un estado que no conocemos NO se mapea a 'pending' en silencio: eso
    // escondería justamente lo que hay que mirar. Se reporta crudo.
    const desconocido = Boolean(statusRemotoRaw) && statusMapeado === undefined;
    if (desconocido) {
      cambios.push({ campo: 'status', local: l.status, remoto: statusRemotoRaw });
    } else if (statusMapeado && statusMapeado !== l.status) {
      cambios.push({ campo: 'status', local: l.status, remoto: statusMapeado });
    }

    if (cambios.length === 0) {
      sinCambios.push(l);
      continue;
    }

    cambiados.push({
      local: l,
      remote: r,
      cambios,
      afectaCosto: cambios.some((c) => c.campo === 'category'),
      rompeEnvio:
        statusMapeado !== undefined &&
        NO_ENVIABLES.has(statusMapeado) &&
        !NO_ENVIABLES.has(l.status),
      desconocido,
    });
  }

  const huerfanos = propios.filter((t) => !vistos.has(clave(t.templateName, t.language)));

  // Lo que rompe el envío va primero: es lo que hay que resolver hoy. Después lo
  // que cuesta plata, y al final lo informativo.
  cambiados.sort((a, b) => peso(b) - peso(a));

  return { nuevos, cambiados, sinCambios, huerfanos };
}

function peso(c: TemplateCambiado): number {
  return (c.rompeEnvio ? 2 : 0) + (c.afectaCosto ? 1 : 0);
}

function clave(templateName: string, language: string): string {
  return `${templateName}__${language}`;
}
